import { useState } from "react";
import { FiUpload, FiStar, FiTrash2, FiSun } from "react-icons/fi";
import { GenerationProfile } from "../../../api/scenarios";
import { parseGenCsv } from "../domain/csv";

interface GenerationMetadata {
  capacity_factor?: number;
  actual_generation_gwh?: number;
  profile_source?: string;
}

interface ProfileManagerProps {
  profiles: GenerationProfile[];
  onUpload: (
    label: string,
    confidenceLevel: number | null,
    data: Array<{ timestamp: string; value: number }>,
    isPrimary: boolean,
  ) => void;
  onDelete: (profileId: string) => void;
  onSetPrimary: (profileId: string) => void;
  samProfileFlag?: boolean;
  generationMetadata?: GenerationMetadata;
}

const PROFILE_LABELS = [
  { label: "P50", confidence: 50 },
  { label: "P75", confidence: 75 },
  { label: "P90", confidence: 90 },
  { label: "P95", confidence: 95 },
  { label: "Custom", confidence: null },
] as const;

const formatMwh = (v: number | null) => {
  if (v == null) return "-";
  if (v >= 1000) return `${(v / 1000).toFixed(1)} GWh`;
  return `${v.toFixed(0)} MWh`;
};

export function ProfileManager({
  profiles,
  onUpload,
  onDelete,
  onSetPrimary,
  samProfileFlag,
  generationMetadata,
}: ProfileManagerProps) {
  const [selectedLabel, setSelectedLabel] = useState("P50");
  const [customLabel, setCustomLabel] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const rows = await parseGenCsv(file);
      if (rows.length === 0) return;

      const labelOption = PROFILE_LABELS.find((l) => l.label === selectedLabel);
      const finalLabel = selectedLabel === "Custom" ? customLabel || "Custom" : selectedLabel;
      const confidence = labelOption?.confidence ?? null;
      const isPrimary = profiles.length === 0; // First profile is auto-primary

      // Map GenPoint (timestamp: string) to { timestamp: string; value: number }
      const mapped = rows.map((r) => ({
        timestamp: r.timestamp,
        value: r.value ?? 0,
      }));

      onUpload(finalLabel, confidence, mapped, isPrimary);
    } finally {
      setIsUploading(false);
      // Reset file input
      e.target.value = "";
    }
  };

  return (
    <div className="rounded-lg border border-border bg-surface-700 p-4">
      <h4 className="text-sm font-semibold text-text-primary mb-3">
        Generation Profiles
      </h4>

      {/* Profile Table */}
      {profiles.length > 0 && (
        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-text-secondary">
                <th className="pb-2 pr-4 font-medium">Label</th>
                <th className="pb-2 pr-4 font-medium">Annual Gen</th>
                <th className="pb-2 pr-4 font-medium">Source</th>
                <th className="pb-2 pr-4 font-medium">Uploaded</th>
                <th className="pb-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => (
                <tr
                  key={p.profile_id}
                  className="border-b border-border/50 hover:bg-surface-600 transition-colors"
                >
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-1.5">
                      {p.is_primary && (
                        <FiStar className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                      )}
                      <span className="font-medium text-text-primary">
                        {p.profile_label}
                      </span>
                      {p.confidence_level && (
                        <span className="text-xs text-text-secondary">
                          ({p.confidence_level}%)
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-text-secondary">
                    {formatMwh(p.annual_generation_mwh)}
                  </td>
                  <td className="py-2 pr-4 text-text-secondary capitalize">
                    {p.source?.replace("_", " ") || "-"}
                  </td>
                  <td className="py-2 pr-4 text-text-secondary">
                    {p.created_at
                      ? new Date(p.created_at).toLocaleDateString()
                      : "-"}
                  </td>
                  <td className="py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {!p.is_primary && (
                        <button
                          onClick={() => onSetPrimary(p.profile_id)}
                          className="p-1 text-text-secondary hover:text-yellow-400 transition-colors"
                          title="Set as primary"
                        >
                          <FiStar className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => onDelete(p.profile_id)}
                        className="p-1 text-text-secondary hover:text-health-400 transition-colors"
                        title="Delete profile"
                      >
                        <FiTrash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {profiles.length === 0 && samProfileFlag && (
        <div className="flex items-start gap-3 rounded-lg border border-quantum-500/30 bg-quantum-500/5 p-3 mb-4">
          <FiSun className="w-5 h-5 text-quantum-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-text-primary">
              Using SAM-generated profile
            </p>
            {generationMetadata ? (
              <p className="text-xs text-text-secondary mt-1">
                Capacity factor: {((generationMetadata.capacity_factor ?? 0) * 100).toFixed(1)}%
                {generationMetadata.actual_generation_gwh != null && (
                  <span className="ml-3">
                    Annual generation: {formatMwh(generationMetadata.actual_generation_gwh * 1000)}
                  </span>
                )}
              </p>
            ) : (
              <p className="text-xs text-text-secondary mt-1">
                Profile will be generated when the model runs
              </p>
            )}
          </div>
        </div>
      )}

      {profiles.length === 0 && !samProfileFlag && (
        <p className="text-sm text-text-secondary mb-4">
          No profiles uploaded yet. Upload a CSV to add confidence-level profiles.
        </p>
      )}

      {/* Upload Section */}
      <div className="flex items-end gap-3">
        <div className="flex-shrink-0">
          <label className="block text-xs font-medium text-text-secondary mb-1">
            Profile Label
          </label>
          <select
            value={selectedLabel}
            onChange={(e) => setSelectedLabel(e.target.value)}
            className="rounded-lg border border-border bg-surface-600 px-3 py-2 text-sm text-text-primary focus:border-quantum-500 focus:outline-none"
          >
            {PROFILE_LABELS.map((opt) => (
              <option key={opt.label} value={opt.label}>
                {opt.label}
                {opt.confidence ? ` (${opt.confidence}%)` : ""}
              </option>
            ))}
          </select>
        </div>

        {selectedLabel === "Custom" && (
          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-text-secondary mb-1">
              Custom Label
            </label>
            <input
              type="text"
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              placeholder="e.g. Optimistic"
              className="rounded-lg border border-border bg-surface-600 px-3 py-2 text-sm text-text-primary w-32 focus:border-quantum-500 focus:outline-none"
            />
          </div>
        )}

        <label
          htmlFor="profile_csv_upload"
          className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer text-sm font-medium transition-colors ${
            isUploading
              ? "text-text-secondary border-border bg-surface-600 cursor-wait"
              : "text-quantum-400 border-quantum-500/40 bg-quantum-500/10 hover:bg-quantum-500/20"
          }`}
        >
          <FiUpload className="w-4 h-4" />
          {isUploading ? "Uploading..." : "Upload CSV"}
        </label>
        <input
          id="profile_csv_upload"
          type="file"
          accept=".csv"
          className="hidden"
          onChange={handleFileUpload}
          disabled={isUploading}
        />
      </div>
    </div>
  );
}
